import React, {memo, useEffect, useMemo, useRef, useState} from 'react'
import {
    Button,
    Field,
    Hotspot,
    Panel,
    PanelWrapper
} from './Elements'
import {gradients} from './gradients'
import {
    bpmToMs,
    maxStagger
} from './utils'

const sectionStyle = {
    border: '1px solid #d0d0d0',
    borderRadius: 4,
    padding: 8,
    backgroundColor: '#fafafa'
}

const sectionTitleStyle = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    marginBottom: 8
}

const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 6
}

const rowLabelStyle = {
    fontSize: 12
}

const helperStyle = {
    fontSize: 11,
    lineHeight: 1.3,
    marginTop: 2,
    marginBottom: 6,
    color: '#555'
}

const fieldStyle = {width: 90}
const checkboxStyle = {width: 'auto'}

const Section = ({title, children}) => (
    <div style={sectionStyle}>
        <div style={sectionTitleStyle}>{title}</div>
        {children}
    </div>
)

const parseLayoutInput = input => {
    const trimmed = input.trim()
    const normalized = trimmed
        .replace(/,\s*([\]}])/g, '$1')
        .replace(/,\s*$/, '')

    try {
        return JSON.parse(normalized)
    } catch {
        const start = normalized.indexOf('[')
        const end = normalized.lastIndexOf(']')
        if (start === -1 || end === -1 || end <= start) {
            throw new Error('Invalid design input')
        }

        return JSON.parse(normalized.slice(start, end + 1))
    }
}

const normalizeSubmissionTitle = input => input
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+/, '')
    .slice(0, 20)

const isValidSubmissionTitle = title => /^[a-z0-9]+(-[a-z0-9]+)*$/.test(title) && title.length <= 20

const GradientSwatches = ({gradient}) => (
    <span style={{display: 'inline-flex', gap: 2}}>
        {gradient.map((color, index) => (
            <span
                key={`${color}-${index}`}
                style={{
                    width: 10,
                    height: 10,
                    border: '1px solid #666',
                    backgroundColor: color,
                    display: 'inline-block'
                }}
            />
        ))}
    </span>
)

const compareHex = (left, right) => left.localeCompare(right, undefined, {sensitivity: 'base'})

const compareGradient = (left, right) => {
    if (left.length !== right.length) {
        return left.length - right.length
    }

    for (let index = 0; index < left.length; index += 1) {
        const result = compareHex(left[index], right[index])
        if (result !== 0) {
            return result
        }
    }

    return 0
}

const Controls = ({
    config,
    onCopy,
    onExportPng,
    onSelectLayoutLabel,
    onSelectApprovedCommunityDesign,
    onSelectGradient,
    onSubmitLayout,
    onToggleCurrentVote,
    onPreviewPendingLayout,
    onModeratePendingLayout,
    onDeletePendingLayout,
    setConfig,
    setValues
}) => {
    const {
        layoutOptions,
        colorStops,
        dimensions,
        squareSize,
        bpm,
        note,
        isLayoutInverted,
        isGradientInverted,
        lockMode,
        showInstructions,
        isUpdating,
        stagger,
        layoutInputValue,
        currentLayoutLabel,
        staggerMode,
        updateMode,
        isCommunityConfigured,
        isCommunityLoading,
        isCommunitySubmitting,
        isCommunityVoting,
        isCommunityModerating,
        communityError,
        communityNotice,
        isCurrentLayoutCommunity,
        currentCommunityVoteCount,
        hasCurrentUserVoted,
        approvedCommunityDesignOptions,
        selectedApprovedCommunityDesignId,
        isCurrentUserAdmin,
        pendingCommunityLayoutOptions,
        pendingCommunityLayoutsById,
        isPendingCommunityLoading
    } = config
    const gradientPickerRef = useRef(null)
    const isEditingLayoutInputRef = useRef(false)
    const [layoutInput, setLayoutInput] = useState(layoutInputValue || '')
    const [submissionTitle, setSubmissionTitle] = useState('')
    const [isSubmittingFromPanel, setIsSubmittingFromPanel] = useState(false)
    const [selectedPendingLayoutId, setSelectedPendingLayoutId] = useState('')

    const selectedGradientIndex = useMemo(() => gradients.findIndex(gradient => (
        gradient.length === colorStops.length && gradient.every((stop, index) => stop === colorStops[index])
    )), [colorStops])
    const sortedGradientOptions = useMemo(() => gradients
        .map((gradient, originalIndex) => ({gradient, originalIndex}))
        .sort((left, right) => {
            const comparison = compareGradient(left.gradient, right.gradient)
            return comparison !== 0 ? comparison : (left.originalIndex - right.originalIndex)
        }), [])
    const selectedGradient = selectedGradientIndex === -1 ? colorStops : gradients[selectedGradientIndex]
    const displayedSelectedGradient = useMemo(() => (
        isGradientInverted ? [...selectedGradient].reverse() : selectedGradient
    ), [isGradientInverted, selectedGradient])

    useEffect(() => {
        if (!isEditingLayoutInputRef.current) {
            setLayoutInput(layoutInputValue || '')
        }
    }, [layoutInputValue])

    useEffect(() => {
        if (!pendingCommunityLayoutOptions.length) {
            setSelectedPendingLayoutId('')
            return
        }

        const hasSelected = pendingCommunityLayoutOptions.some(option => option.value === selectedPendingLayoutId)
        if (!hasSelected) {
            setSelectedPendingLayoutId('')
        }
    }, [pendingCommunityLayoutOptions, selectedPendingLayoutId])

    const toggleRandomize = () => setConfig(currentConfig => ({...currentConfig, isUpdating: !currentConfig.isUpdating}))

    const copyToClipboard = () => onCopy()
    const downloadPng = () => onExportPng()
    const selectGradient = index => {
        onSelectGradient(index)
        if (gradientPickerRef.current) {
            gradientPickerRef.current.open = false
        }
    }

    const processInput = () => {
        try {
            const newLayout = parseLayoutInput(layoutInput)

            if (Array.isArray(newLayout) && newLayout.length === dimensions * dimensions)
                setValues(newLayout)
        } catch {
            // Ignore invalid input so submit never crashes the UI.
        }
    }

    const submitCurrentLayout = async () => {
        if (!onSubmitLayout || isSubmittingFromPanel || isCommunitySubmitting) {
            return
        }

        setIsSubmittingFromPanel(true)
        try {
            const normalizedTitle = normalizeSubmissionTitle(submissionTitle.trim())
            const result = await onSubmitLayout({
                title: normalizedTitle
            })

            if (result?.ok) {
                setSubmissionTitle('')
            }
        } finally {
            setIsSubmittingFromPanel(false)
        }
    }

    const normalizedSubmissionTitle = normalizeSubmissionTitle(submissionTitle.trim())
    const isSubmissionTitleValid = isValidSubmissionTitle(normalizedSubmissionTitle)
    const isSubmissionBusy = isCommunitySubmitting || isSubmittingFromPanel
    const canSubmitCurrentLayout = Boolean(isSubmissionTitleValid && !isSubmissionBusy)
    const selectedPendingLayout = selectedPendingLayoutId ? pendingCommunityLayoutsById[selectedPendingLayoutId] : null
    const canModeratePendingLayout = Boolean(
        selectedPendingLayoutId &&
        isCurrentUserAdmin &&
        !isCommunityModerating &&
        !isPendingCommunityLoading
    )
    const canPromoteApprovedDesign = Boolean(
        selectedApprovedCommunityDesignId &&
        isCurrentUserAdmin &&
        !isCommunityModerating
    )

    const moderatePendingLayout = nextStatus => {
        if (!selectedPendingLayoutId || !onModeratePendingLayout) {
            return
        }

        onModeratePendingLayout({
            submissionId: selectedPendingLayoutId,
            nextStatus
        })
    }
    const promoteApprovedDesign = () => {
        if (!selectedApprovedCommunityDesignId || !onModeratePendingLayout) {
            return
        }

        onModeratePendingLayout({
            submissionId: selectedApprovedCommunityDesignId,
            nextStatus: 'promoted'
        })
    }
    const selectPendingLayout = event => {
        const nextSubmissionId = event.target.value
        setSelectedPendingLayoutId(nextSubmissionId)

        if (!nextSubmissionId || !onPreviewPendingLayout) {
            return
        }

        onPreviewPendingLayout(nextSubmissionId)
    }
    const deletePendingLayout = () => {
        if (!selectedPendingLayoutId || !onDeletePendingLayout) {
            return
        }

        onDeletePendingLayout(selectedPendingLayoutId)
    }

    return (
        <PanelWrapper>
            <Hotspot aria-label="Toggle controls"/>
            <Panel>
                <Section title="Auto-Update">
                    <div style={rowStyle}>
                        <span style={rowLabelStyle}>Mode</span>
                        <select
                          onChange={e => setConfig(currentConfig => ({...currentConfig, updateMode: e.target.value}))}
                          value={updateMode}
                        >
                            <option value="single">single square - random</option>
                            <option value="all">all squares - random</option>
                            <option value="layout">predefined designs</option>
                        </select>
                    </div>
                    <div style={{...rowStyle, justifyContent: 'center'}}>
                        <Button onClick={toggleRandomize}>
                            Auto Updating: {isUpdating ? 'on' : 'off'}
                        </Button>
                    </div>
                    <div style={rowStyle}>
                        <span style={rowLabelStyle}>Lock</span>
                        <select
                          onChange={e => setConfig(currentConfig => ({...currentConfig, lockMode: e.target.value}))}
                          value={lockMode}
                        >
                            <option value="none">none</option>
                            <option value="colors">colors</option>
                            <option value="layout">design</option>
                        </select>
                    </div>
                    <div style={rowStyle}>
                        <span style={rowLabelStyle}>BPM</span>
                        <Field
                            onChange={e => setConfig(currentConfig => ({
                                ...currentConfig,
                                bpm: e.target.value,
                                interval: bpmToMs(e.target.value, note),
                                stagger: Math.min(stagger, maxStagger(bpmToMs(e.target.value, note), dimensions))
                            }))}
                            style={fieldStyle}
                            value={bpm}
                            type="number"
                        />
                    </div>
                    <div style={rowStyle}>
                        <span style={rowLabelStyle}>Bar</span>
                        <select
                            onChange={e => setConfig(currentConfig => ({
                                ...currentConfig,
                                note: Number(e.target.value),
                                interval: bpmToMs(bpm, Number(e.target.value)),
                                stagger: Math.min(stagger, maxStagger(bpmToMs(bpm, Number(e.target.value)), dimensions))
                            }))}
                            value={note}
                        >
                            <option value={1}>whole</option>
                            <option value={.5}>half</option>
                            <option value={.25}>quarter</option>
                        </select>
                    </div>
                </Section>

                <Section title="Configuration">
                    <div style={rowStyle}>
                        <span style={rowLabelStyle}>Design</span>
                        <select
                          disabled={!layoutOptions.length}
                          onChange={e => onSelectLayoutLabel(e.target.value)}
                          value={currentLayoutLabel ?? ''}
                        >
                            {!currentLayoutLabel && <option value="">Select design</option>}
                            {layoutOptions.map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                    <div style={rowStyle}>
                        <span style={rowLabelStyle}>Invert Design</span>
                        <Field
                            onChange={() => setConfig(currentConfig => ({
                                ...currentConfig,
                                isLayoutInverted: !currentConfig.isLayoutInverted
                            }))}
                            checked={isLayoutInverted}
                            style={checkboxStyle}
                            type="checkbox"
                        />
                    </div>
                    <div style={rowStyle}>
                        <span style={rowLabelStyle}>Colors</span>
                        <details ref={gradientPickerRef} style={{position: 'relative'}}>
                            <summary style={{cursor: 'pointer'}}>
                                <GradientSwatches gradient={displayedSelectedGradient}/>
                            </summary>
                            <div style={{
                                position: 'absolute',
                                right: 0,
                                top: '100%',
                                width: 188,
                                maxHeight: 160,
                                overflowY: 'auto',
                                border: '1px solid #333',
                                backgroundColor: '#fff',
                                padding: 4,
                                zIndex: 1200
                            }}>
                                {sortedGradientOptions.map(({gradient, originalIndex}) => (
                                  <button
                                    key={originalIndex}
                                    type="button"
                                    onClick={() => selectGradient(originalIndex)}
                                    style={{
                                        width: '100%',
                                        textAlign: 'left',
                                        background: '#fff',
                                        border: selectedGradientIndex === originalIndex ? '1px solid #333' : '1px solid transparent',
                                        padding: 2,
                                        marginBottom: 2,
                                        cursor: 'pointer'
                                    }}
                                  >
                                      <GradientSwatches gradient={isGradientInverted ? [...gradient].reverse() : gradient}/>
                                  </button>
                                ))}
                            </div>
                        </details>
                    </div>
                    <div style={rowStyle}>
                        <span style={rowLabelStyle}>Invert Gradient</span>
                        <Field
                            onChange={() => setConfig(currentConfig => ({
                                ...currentConfig,
                                isGradientInverted: !currentConfig.isGradientInverted
                            }))}
                            checked={isGradientInverted}
                            style={checkboxStyle}
                            type="checkbox"
                        />
                    </div>
                    <div style={rowStyle}>
                        <span style={rowLabelStyle}>Stagger Mode</span>
                        <select
                          onChange={e => setConfig(currentConfig => ({
                              ...currentConfig,
                              staggerMode: e.target.value
                          }))}
                          value={staggerMode}
                        >
                            <option value="off">off</option>
                            <option value="outward">outward</option>
                            <option value="inward">inward</option>
                        </select>
                    </div>
                    <div style={{...rowStyle}}>
                        <span style={rowLabelStyle}>Stagger (ms)</span>
                        <Field
                          onChange={e => setConfig(currentConfig => ({...currentConfig, stagger: e.target.value}))}
                          style={fieldStyle}
                          value={stagger}
                          type="number"
                        />
                    </div>
                    <div style={rowStyle}>
                        <span style={rowLabelStyle}>Square Size (px)</span>
                        <Field
                          onChange={e => setConfig(currentConfig => ({...currentConfig, squareSize: e.target.value}))}
                          style={fieldStyle}
                          value={squareSize}
                          type="number"
                        />
                    </div>
                    <div style={{...rowStyle, marginBottom: 0}}>
                        <span style={rowLabelStyle}>Instructions</span>
                        <Field
                          onChange={() => setConfig(currentConfig => ({
                              ...currentConfig,
                              showInstructions: !currentConfig.showInstructions
                          }))}
                          checked={showInstructions}
                          style={checkboxStyle}
                          type="checkbox"
                        />
                    </div>
                </Section>

                <Section title="Submit Your Design">
                    <div style={helperStyle}>
                        {isCommunityConfigured ? (
                            'Submit your current design for review.'
                        ) : (
                            'Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to enable submissions and voting.'
                        )}
                    </div>
                    <div style={{...rowStyle, marginBottom: 8}}>
                        <span style={rowLabelStyle}>Title</span>
                        <Field
                          disabled={!isCommunityConfigured || isSubmissionBusy}
                          onChange={event => setSubmissionTitle(normalizeSubmissionTitle(event.target.value))}
                          style={fieldStyle}
                          value={submissionTitle}
                          type="text"
                          maxLength={20}
                        />
                    </div>
                    <div style={{...helperStyle, marginTop: 0}}>
                        Use lowercase letters, numbers, and dashes only (max 20 chars).
                    </div>
                    <div style={{...rowStyle, justifyContent: 'center', marginBottom: 0}}>
                        <Button disabled={!canSubmitCurrentLayout} onClick={submitCurrentLayout}>
                            {isCommunitySubmitting ? 'Submitting...' : 'Submit'}
                        </Button>
                    </div>
                </Section>

                <Section title="Community Designs">
                    <div style={helperStyle}>
                        {isCommunityConfigured ? (
                            isCommunityLoading ? 'Loading community designs...' : 'Browse approved designs and vote.'
                        ) : (
                            'Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to enable submissions and voting.'
                        )}
                    </div>
                    <div style={rowStyle}>
                        <span style={rowLabelStyle}>Submitted Designs</span>
                        <select
                          disabled={!isCommunityConfigured || isCommunityLoading || !approvedCommunityDesignOptions.length}
                          onChange={event => onSelectApprovedCommunityDesign(event.target.value)}
                          value={selectedApprovedCommunityDesignId}
                        >
                            {!approvedCommunityDesignOptions.length && <option value="">No approved designs</option>}
                            {!!approvedCommunityDesignOptions.length && <option value="">Select a design</option>}
                            {approvedCommunityDesignOptions.map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                    {isCurrentLayoutCommunity && (
                      <>
                          <div style={{...rowStyle, marginBottom: 2}}>
                              <span style={rowLabelStyle}>Votes</span>
                              <span style={rowLabelStyle}>{currentCommunityVoteCount}</span>
                          </div>
                          <div style={{...rowStyle, justifyContent: 'center', marginBottom: 4}}>
                              <Button
                                disabled={!isCommunityConfigured || isCommunityVoting}
                                onClick={onToggleCurrentVote}
                              >
                                  {isCommunityVoting
                                    ? 'Updating Vote...'
                                    : hasCurrentUserVoted
                                      ? 'Remove Vote'
                                      : 'Upvote'}
                              </Button>
                          </div>
                          {isCurrentUserAdmin && (
                            <div style={{...rowStyle, justifyContent: 'center', marginBottom: 4}}>
                                <Button disabled={!canPromoteApprovedDesign} onClick={promoteApprovedDesign}>
                                    {isCommunityModerating ? 'Working...' : 'Promote Approved'}
                                </Button>
                            </div>
                          )}
                      </>
                    )}
                    {isCurrentUserAdmin && (
                      <>
                          <div style={{...rowStyle, marginTop: 6}}>
                              <span style={rowLabelStyle}>Pending</span>
                              <select
                                disabled={isPendingCommunityLoading || isCommunityModerating || !pendingCommunityLayoutOptions.length}
                                onChange={selectPendingLayout}
                                value={selectedPendingLayoutId}
                              >
                                  {!pendingCommunityLayoutOptions.length && <option value="">No pending</option>}
                                  {!!pendingCommunityLayoutOptions.length && <option value="">Select a design</option>}
                                  {pendingCommunityLayoutOptions.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                  ))}
                              </select>
                          </div>
                          {selectedPendingLayout?.preview_image_url && (
                            <div style={{...helperStyle, marginTop: 0}}>
                                Preview image: <a href={selectedPendingLayout.preview_image_url} target="_blank" rel="noreferrer">open</a>
                            </div>
                          )}
                          <div style={{...rowStyle, justifyContent: 'center'}}>
                              <Button disabled={!canModeratePendingLayout} onClick={() => moderatePendingLayout('approved')}>
                                  {isCommunityModerating ? 'Working...' : 'Approve Pending'}
                              </Button>
                          </div>
                          <div style={{...rowStyle, justifyContent: 'center'}}>
                              <Button disabled={!canModeratePendingLayout} onClick={deletePendingLayout}>
                                  {isCommunityModerating ? 'Working...' : 'Delete Pending'}
                              </Button>
                          </div>
                      </>
                    )}
                    {!!communityNotice && <div style={{...helperStyle, color: '#0f5132'}}>{communityNotice}</div>}
                    {!!communityError && <div style={{...helperStyle, color: '#842029'}}>{communityError}</div>}
                </Section>

                <Section title="Design Editing">
                    <div style={{...rowStyle, justifyContent: 'center'}}>
                        <Button onClick={copyToClipboard}>Copy Design to Clipboard</Button>
                    </div>
                    <div style={rowStyle}>
                        <span style={rowLabelStyle}>Design Input</span>
                        <Field
                            onBlur={() => {
                                isEditingLayoutInputRef.current = false
                            }}
                            onChange={e => setLayoutInput(e.target.value)}
                            onFocus={e => {
                                isEditingLayoutInputRef.current = true
                                e.target.select()
                            }}
                            style={fieldStyle}
                            value={layoutInput}
                            type="text"
                        />
                    </div>
                    <div style={{...rowStyle, justifyContent: 'center'}}>
                        <Button onClick={processInput}>Update Design</Button>
                    </div>
                    <div style={{...rowStyle, justifyContent: 'center', marginBottom: 0}}>
                        <Button onClick={downloadPng}>Download PNG</Button>
                    </div>
                </Section>
            </Panel>
        </PanelWrapper>
    )
}

export default memo(Controls)
